import React, { useContext, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { M_Button, BufferService, LoadingContext, M_StateActions, NavigateContext, M_Header, M_SimpleTable, MSwitchButton, M_MessageBoard, M_TextArea } from 'src/components/M_Components'
import { useDispatch, useSelector } from 'react-redux'
import { Chip } from '@mui/material'
import { CheckCircle, Edit, Close, Visibility, Description, Done, Clear } from '@mui/icons-material'

export default function CmrDocuments() {
    const dispatch = useDispatch()
    const M_State = useSelector((state) => state.StateKeyValue)
    const [id, setId] = useState(M_State.filter(obj => obj.key === 'selectedTab')[0]?.value)
    const [cmrDocuments, setCmrDocuments] = useState([])
    const [isFirstLoop, setIsFirstLoop] = useState(true)
    const [autoRefresh, setAutoRefresh] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [dialogType, setDialogType] = useState(null) // 'approve', 'update', 'close'
    const [selectedCmrDocument, setSelectedCmrDocument] = useState(null)
    const [driverNotes, setDriverNotes] = useState('')
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)
    const { t } = useTranslation()
    const bufferServiceCmr = new BufferService('CmrDocument')
    const sessionKeys = ['id', 'cmrDocuments', 'autoRefresh']

    useEffect(() => {
        if (isFirstLoop) {
            ctx.setIsLoad(true)
            getSessionStorageData(id)
            setIsFirstLoop(false)
        }
        // eslint-disable-next-line
        let interval

        if (autoRefresh) {
            interval = setInterval(() => {
                reloadCmrDocuments()
            }, 10000)
        }

        return () => clearInterval(interval)
        // eslint-disable-next-line
    }, [autoRefresh, isFirstLoop, id])

    useEffect(() => {
        const newId = M_State.filter(obj => obj.key === 'selectedTab')[0]?.value
        if (newId !== id) {
            setId(newId)
            getSessionStorageData(newId)
        }
    }, [M_State, id])

    useEffect(() => {
        const tempArray = []
        sessionKeys.forEach((key) => {
            tempArray.push(sessionStorageMachine(id, key, key))
        })
        dispatch(M_StateActions.addM_State({ key: id, value: tempArray }))
    }, [id, cmrDocuments, autoRefresh])

    const sessionStorageMachine = (id, key, value) => {
        let tempObj = {}
        // eslint-disable-next-line
        if (eval(value) instanceof Map) {
            // eslint-disable-next-line
            tempObj = { key: `${key}`, value: (Array.from(eval(value))) }
        } else {
            // eslint-disable-next-line
            tempObj = { key: `${key}`, value: (eval(value)) }
        }
        return tempObj
    }

    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true)
        const M_StateData = M_State.filter(obj => obj.key === sessionID)[0]?.value
        if (M_StateData) {
            const tempCD = M_StateData.filter(obj => obj.key === `cmrDocuments`)[0]?.value
            const tempAR = M_StateData.filter(obj => obj.key === `autoRefresh`)[0]?.value
            if (tempCD || tempAR !== undefined) {
                if (tempCD) setCmrDocuments(tempCD)
                if (tempAR !== undefined) setAutoRefresh(tempAR)
                ctx.setIsLoad(false)
                return
            }
        }
        loadCmrDocuments()
    }

    const loadCmrDocuments = async () => {
        try {
            ctx.setIsLoad(true)
            const response = await bufferServiceCmr.getList()
            // Check if response is an array (successful response) or an error object
            if (Array.isArray(response)) {
                setCmrDocuments(response)
            } else if (response?.name === 'AxiosError') {
                ctx.toastMessages('error', response.response?.data?.Errors?.[0] || 'Error loading CMR documents', '')
                setCmrDocuments([])
            } else {
                // Handle case where response might be wrapped or in unexpected format
                console.log('Unexpected response format:', response)
                ctx.toastMessages('error', 'Error loading CMR documents', '')
                setCmrDocuments([])
            }
        } catch (error) {
            console.error('Error loading CMR documents:', error)
            ctx.toastMessages('error', 'Error loading CMR documents', '')
            setCmrDocuments([])
        } finally {
            ctx.setIsLoad(false)
        }
    }

    const reloadCmrDocuments = useCallback(() => {
        ctx.toastMessages("info", '', t("GetNewCmrDocuments") || "Refreshing CMR documents...")
        bufferServiceCmr.getList().then((response) => {
            if (Array.isArray(response)) {
                setCmrDocuments(response)
                ctx.setIsLoad(false)
                ctx.toastMessages('success', '', t("GetCmrDocuments") || "CMR documents loaded successfully")
            } else if (response?.name === 'AxiosError') {
                ctx.toastMessages('error', response.response?.data?.Errors?.[0] || 'Error loading CMR documents', '')
                ctx.setIsLoad(false)
            } else {
                console.log('Unexpected response format:', response)
                ctx.toastMessages('error', 'Error loading CMR documents', '')
                ctx.setIsLoad(false)
            }
        }).catch((error) => {
            console.error('Error reloading CMR documents:', error)
            ctx.toastMessages('error', 'Error reloading CMR documents', '')
            ctx.setIsLoad(false)
        })
    }, [bufferServiceCmr, ctx, t])

    const changeAutoRefresh = () => {
        setAutoRefresh(prev => !prev)
    }

    const getStateColor = (state) => {
        switch (state) {
            case 0: // Draft
                return 'default'
            case 1: // Generated
                return 'info'
            case 2: // Approved
                return 'success'
            case 3: // Updated
                return 'warning'
            case 4: // Closed
                return 'secondary'
            default:
                return 'default'
        }
    }

    const getStateLabel = (state) => {
        switch (state) {
            case 0:
                return 'Draft'
            case 1:
                return 'Generated'
            case 2:
                return 'Approved'
            case 3:
                return 'Updated'
            case 4:
                return 'Closed'
            default:
                return 'Unknown'
        }
    }

    const openDialog = (type, cmrDocument) => {
        setDialogType(type)
        setSelectedCmrDocument(cmrDocument)
        setDriverNotes(cmrDocument.DriverNotes || '')
        setDialogOpen(true)
    }

    const closeDialog = () => {
        setDialogOpen(false)
        setDialogType(null)
        setSelectedCmrDocument(null)
        setDriverNotes('')
    }

    const handleApprove = (cmrDocument) => {
        openDialog('approve', cmrDocument)
    }

    const handleClose = (cmrDocument) => {
        openDialog('close', cmrDocument)
    }

    const executeAction = async () => {
        if (!selectedCmrDocument) return

        try {
            ctx.setIsLoad(true)
            const requestBody = {
                cmrDocumentId: selectedCmrDocument.CmrDocumentId,
                serviceOrderId: selectedCmrDocument.ServiceOrderId,
                driverNotes: driverNotes || ''
            }

            let endpoint = ''
            let successMessage = ''
            let errorMessage = ''

            switch (dialogType) {
                case 'approve':
                    endpoint = `/${selectedCmrDocument.CmrDocumentId}/approve`
                    successMessage = 'CMR document approved successfully'
                    errorMessage = 'Error approving CMR document'
                    break
                case 'update':
                    endpoint = `/${selectedCmrDocument.CmrDocumentId}/update`
                    successMessage = 'CMR document updated successfully'
                    errorMessage = 'Error updating CMR document'
                    break
                case 'close':
                    endpoint = `/${selectedCmrDocument.CmrDocumentId}/close`
                    successMessage = 'CMR document closed successfully'
                    errorMessage = 'Error closing CMR document'
                    break
                default:
                    return
            }

            const response = await bufferServiceCmr.postPath(endpoint, requestBody)
            if (response && !response.response) {
                ctx.toastMessages('success', successMessage, '')
                closeDialog()
                await loadCmrDocuments()
            } else {
                ctx.toastMessages('error', errorMessage, '')
            }
        } catch (error) {
            console.error(`Error ${dialogType}ing CMR:`, error)
            ctx.toastMessages('error', `Error ${dialogType}ing CMR document`, '')
        } finally {
            ctx.setIsLoad(false)
        }
    }

    const getDialogTitle = () => {
        switch (dialogType) {
            case 'approve':
                return t('Approve CMR Document') || 'Approve CMR Document'
            case 'update':
                return t('Update CMR Document') || 'Update CMR Document'
            case 'close':
                return t('Close CMR Document') || 'Close CMR Document'
            default:
                return ''
        }
    }

    const handleView = (cmrDocument) => {
        ctx.passValue.set('cmrDocument', cmrDocument)
        ctx2.newTab('viewCmrDocument', `CMR ${cmrDocument.DocumentNumber}`)
    }

    const renderState = (row) => {
        return (
            <Chip
                label={getStateLabel(row.State)}
                color={getStateColor(row.State)}
                size="small"
            />
        )
    }

    const renderEcmrStatus = (row) => {
        return row.IsEcmrIntegrated ? (
            <Chip label="Yes" color="success" size="small" />
        ) : (
            <Chip label="No" color="default" size="small" />
        )
    }

    const renderActions = (row) => {
        return (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <M_Button
                    label="View"
                    onClick={() => handleView(row)}
                    icon={<Visibility />}
                    size="small"
                />
                {row.State === 1 || row.State === 3 ? (
                    <>
                        <M_Button
                            label="Approve"
                            onClick={() => handleApprove(row)}
                            icon={<CheckCircle />}
                            size="small"
                            color="success"
                        />
                    </>
                ) : null}
                {row.State !== 4 ? (
                    <M_Button
                        label="Close"
                        onClick={() => handleClose(row)}
                        icon={<Close />}
                        size="small"
                        color="error"
                    />
                ) : null}
            </div>
        )
    }

    const columns = [
        { field: 'DocumentNumber', header: t('DocumentNumber') || 'Document Number', type: 'string' },
        { field: 'ServiceOrderDocNumber', header: t('ServiceOrder') || 'Service Order', type: 'string' },
        { field: 'CustomerName', header: t('Customer') || 'Customer', type: 'string' },
        { field: 'StateDisplayName', header: t('State') || 'State', type: 'string', body: renderState },
        { field: 'IssueDateTime', header: t('IssueDate') || 'Issue Date', type: 'date' },
        { field: 'StateChangedAt', header: t('LastModified') || 'Last Modified', type: 'date' },
        { field: 'IsEcmrIntegrated', header: t('eCMR Integrated') || 'eCMR Integrated', type: 'boolean', body: renderEcmrStatus },
        { field: 'actions', header: t('Actions') || 'Actions', body: renderActions }
    ]

    return (
        <div>
            <M_Header
                title={t('CMR Documents')}
                icon={<Description />}
                element={
                    <>
                        <div className='divHeaderListOrderService' style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <M_Button
                                tooltip={t("RefreshCmrDocuments") || t("Refresh")}
                                icon={<img src="/icons/svgsDaNet/refresh.svg"
                                    alt="Refresh"
                                    className='iconsMenuListOrderService'
                                />}
                                label={<b>{t("RefreshCmrDocuments") || t("Refresh")}</b>}
                                onClick={() => reloadCmrDocuments()}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '15px' }}>
                                <MSwitchButton
                                    label={"Auto-Refresh"}
                                    labelColor={'var(--color-black)'}
                                    onChange={changeAutoRefresh}
                                    checked={autoRefresh}
                                    isBold
                                    fontSize={'14px'}
                                />
                            </div>
                        </div>
                    </>
                }
            />

            <div style={{ marginTop: '20px' }}>
                <M_SimpleTable
                    rows={cmrDocuments}
                    columns={columns}
                />
            </div>

            {/* Driver Notes Dialog */}
            <M_MessageBoard
                open={dialogOpen}
                onClose={closeDialog}
                title={getDialogTitle()}
                width="500px"
                minWidth="400px"
                body={
                    <div style={{ padding: '10px 0' }}>
                        <M_TextArea
                            label={t('Driver Notes') || 'Driver Notes'}
                            value={driverNotes}
                            onChange={(e) => setDriverNotes(e.target.value)}
                            rows={4}
                            helperText={t('Optional notes from the driver') || 'Optional notes from the driver'}
                        />
                    </div>
                }
                footer={
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                        <M_Button
                            label={t('ButtonCancel') || 'Cancel'}
                            icon={<Clear style={{ color: 'var(--color-white)' }} />}
                            backgroundColor="var(--color-red)"
                            labelColor="var(--color-white)"
                            onClick={closeDialog}
                        />
                        <M_Button
                            label={t('ButtonConfirm') || 'Confirm'}
                            icon={<Done style={{ color: 'var(--color-white)' }} />}
                            backgroundColor="var(--color-green)"
                            labelColor="var(--color-white)"
                            onClick={executeAction}
                        />
                    </div>
                }
            />
        </div>
    )
}

